import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import {
  Typography,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Box,
  CircularProgress,
} from "@mui/material";
import PlayerCardRow from "./PlayerCardRow";
import PlayerSearchBar from "./PlayerSearchBar";
import { teamHeaderGradient } from "./utils/teamColors";

interface StatItem {
  label: string;
  value: number;
  aaa_p: number;
  mlb_p: number;
  mlb_avg: number;
  higher_better: boolean;
}

interface ContextData {
  name: string;
  id: string;
  level: string;
  season: string;
  team: string;
  position: string;
  stats: StatItem[];
}

const RATE_LABELS = ["xwOBA", "xBA", "xSLG"];
const MPH_LABELS = ["EV", "Max EV"];

function fmtVal(value: number, label: string): string {
  if (RATE_LABELS.includes(label)) return value.toFixed(3);
  if (MPH_LABELS.includes(label)) return value.toFixed(1) + " mph";
  return value.toFixed(1) + "%";
}

// Shorter version for slider rows — omits " mph" unit since the label already implies it
function fmtSliderVal(value: number, label: string): string {
  if (RATE_LABELS.includes(label)) return value.toFixed(3);
  if (MPH_LABELS.includes(label)) return value.toFixed(1);
  return value.toFixed(1) + "%";
}

function fmtDiff(d: number, label: string): string {
  const sign = d > 0 ? "+" : "";
  if (RATE_LABELS.includes(label)) return sign + d.toFixed(3);
  if (MPH_LABELS.includes(label)) return sign + d.toFixed(1);
  return sign + d.toFixed(1) + "%";
}

const BOX_PAPER_SX = {
  border: "#293241 solid 2px",
  boxShadow: "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black",
  display: "flex",
  flexDirection: "column" as const,
  height: "100%",
  overflow: "hidden",
  minWidth: 0,
};

function BoxHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <Box sx={{ backgroundColor: "#3D5A80", px: 2, py: 1.25 }}>
      <Typography variant="subtitle1" color="white" fontWeight="bold" textAlign="center" lineHeight={1.3}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="#ABB8C8" display="block" textAlign="center">
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function CompareRow({
  stat,
  playerVal,
  mlbAvg,
  higherBetter,
}: {
  stat: string;
  playerVal: number;
  mlbAvg: number;
  higherBetter: boolean;
}) {
  const diff = playerVal - mlbAvg;
  const isNeutral = Math.abs(diff) < 0.0005;
  const isBetter = higherBetter ? diff >= 0 : diff <= 0;
  const diffColor = isNeutral ? "#888" : isBetter ? "#1a7c1a" : "#c0392b";

  return (
    <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.5 }}>
      <Typography variant="body2" sx={{ flex: "0 0 30%", textAlign: "right", pr: 1, color: "text.secondary" }}>
        {stat}
      </Typography>
      <Typography variant="body2" sx={{ flex: "0 0 23%", textAlign: "center", fontWeight: "bold" }}>
        {fmtVal(playerVal, stat)}
      </Typography>
      <Typography variant="body2" sx={{ flex: "0 0 23%", textAlign: "center", color: "text.secondary" }}>
        {fmtVal(mlbAvg, stat)}
      </Typography>
      <Typography variant="body2" sx={{ flex: "0 0 24%", textAlign: "center", fontWeight: "bold", color: diffColor }}>
        {fmtDiff(diff, stat)}
      </Typography>
    </Box>
  );
}

export default function MlbContext(props: any) {
  const { id } = useParams();
  const [season, setSeason] = React.useState("");
  const [availableSeasons, setAvailableSeasons] = React.useState<string[]>([]);
  const [data, setData] = React.useState<ContextData | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  // Fetch available seasons for this player on mount
  useEffect(() => {
    if (!id) return;
    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";
    fetch(`${base}/player-seasons/${id}/`)
      .then((r) => r.json())
      .then((d) => {
        if (d.seasons?.length) {
          setAvailableSeasons(d.seasons);
          setSeason(d.latest);
        } else {
          // fallback: show all seasons, default to 2026
          setAvailableSeasons(["2023", "2024", "2025", "2026"]);
          setSeason("2026");
        }
      })
      .catch(() => {
        setAvailableSeasons(["2023", "2024", "2025", "2026"]);
        setSeason("2026");
      });
  }, [id, props.prod]);

  useEffect(() => {
    if (!id || !season) return;
    setLoading(true);
    setError("");
    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";
    fetch(`${base}/mlb-context/${id}/${season}/`)
      .then((res) => res.json())
      .then((d) => {
        if (d.error) { setError(d.error); setData(null); }
        else setData(d);
        setLoading(false);
      })
      .catch((e) => { setError(String(e)); setLoading(false); });
  }, [id, season, props.prod]);

  const isPitcher = data?.position === "P";
  const levelLabel = data ? `${data.level} ${data.season}` : "";
  const peerSubtitle = data ? `vs ${data.level} peers · ${data.season}` : "";
  const mlbSubtitle = data ? `${data.season} MLB · ≥50 PA` : "";

  return (
    <div style={{ width: "100%", padding: "16px", boxSizing: "border-box", overflowX: "hidden" }}>
      <title>Prospect Savant – MLB Context</title>

      {/* Header */}
      <Paper
        square
        variant="outlined"
        sx={{
          mb: 2.5,
          overflow: "hidden",
          border: "#293241 solid 2px",
          boxShadow: "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
        }}
      >
        <Box
          sx={{
            background: teamHeaderGradient(data?.team),
            px: { xs: 2, md: 3 },
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {data && id && (
            <Avatar
              src={`https://img.mlbstatic.com/mlb-photos/image/upload/c_fill,g_auto/w_120/v1/people/${id}/headshot/milb/current`}
              alt={data.name}
              sx={{ width: 56, height: 56, border: "2px solid #fff", bgcolor: "#fff" }}
            />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight="bold" color="#fff" sx={{ lineHeight: 1.1 }}>
              {data ? (
                <a href={`/player/${id}`} style={{ textDecoration: "none", color: "inherit" }}>
                  {data.name}
                </a>
              ) : (
                "MLB Context"
              )}
            </Typography>
            {data && (
              <Typography variant="subtitle2" sx={{ color: "#E0FBFC", fontWeight: 400 }}>
                {levelLabel} MLB Context {isPitcher ? "(Pitcher)" : "(Hitter)"}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: { md: "auto" }, flexWrap: "wrap" }}>
            <FormControl sx={{ minWidth: 110, bgcolor: "#fff", borderRadius: 1 }} size="small">
              <InputLabel>Season</InputLabel>
              <Select value={season} label="Season" onChange={(e: SelectChangeEvent) => setSeason(e.target.value)}>
                {availableSeasons.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <PlayerSearchBar prod={props.prod} urlPrefix="/mlb-context" placeholder="Switch player…" />
          </Box>
        </Box>
      </Paper>

      {loading && <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>}
      {error && <Typography color="error" ml={2}>{error}</Typography>}

      {data && !loading && (
        <Grid container spacing={2} alignItems="stretch">
          {/* Box 1 — AAA Percentiles */}
          <Grid size={props.isDesktop ? 4 : 12} sx={{ display: "flex", flexDirection: "column" }}>
            <Paper variant="outlined" square sx={BOX_PAPER_SX}>
              <BoxHeader title={`${data.level} Percentiles`} subtitle={peerSubtitle} />
              <Box sx={{ flex: 1, px: 1 }}>
                {data.stats.map((s, i) => (
                  <React.Fragment key={s.label}>
                    <PlayerCardRow
                      stat={s.label}
                      value={fmtSliderVal(s.value, s.label)}
                      percentile={Math.round(s.aaa_p * 100)}
                      percentile_raw={s.aaa_p}
                      isDesktop={props.isDesktop}
                    />
                    {i < data.stats.length - 1 && <Divider variant="middle" />}
                  </React.Fragment>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Box 2 — MLB Equivalent Percentiles */}
          <Grid size={props.isDesktop ? 4 : 12} sx={{ display: "flex", flexDirection: "column" }}>
            <Paper variant="outlined" square sx={BOX_PAPER_SX}>
              <BoxHeader title="MLB Equivalent Percentiles" subtitle="if these stats were in MLB" />
              <Box sx={{ flex: 1, px: 1 }}>
                {data.stats.map((s, i) => (
                  <React.Fragment key={s.label}>
                    <PlayerCardRow
                      stat={s.label}
                      value={fmtSliderVal(s.value, s.label)}
                      percentile={Math.round(s.mlb_p * 100)}
                      percentile_raw={s.mlb_p}
                      isDesktop={props.isDesktop}
                    />
                    {i < data.stats.length - 1 && <Divider variant="middle" />}
                  </React.Fragment>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Box 3 — vs MLB Average */}
          <Grid size={props.isDesktop ? 4 : 12} sx={{ display: "flex", flexDirection: "column" }}>
            <Paper variant="outlined" square sx={BOX_PAPER_SX}>
              <BoxHeader title="vs MLB Average" subtitle={mlbSubtitle} />
              {/* Column headers */}
              <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.75, backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
                <Typography variant="caption" sx={{ flex: "0 0 30%", textAlign: "right", pr: 1, color: "text.secondary" }}>Stat</Typography>
                <Typography variant="caption" sx={{ flex: "0 0 23%", textAlign: "center", fontWeight: "bold" }}>Player</Typography>
                <Typography variant="caption" sx={{ flex: "0 0 23%", textAlign: "center", color: "text.secondary" }}>MLB Avg</Typography>
                <Typography variant="caption" sx={{ flex: "0 0 24%", textAlign: "center", color: "text.secondary" }}>Diff</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                {data.stats.map((s, i) => (
                  <React.Fragment key={s.label}>
                    <CompareRow
                      stat={s.label}
                      playerVal={s.value}
                      mlbAvg={s.mlb_avg}
                      higherBetter={s.higher_better}
                    />
                    {i < data.stats.length - 1 && <Divider variant="middle" />}
                  </React.Fragment>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </div>
  );
}
